import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { tap, finalize, catchError } from 'rxjs/operators';
import { UserInfo } from 'src/app/model/User';
import { ActivityService } from 'src/app/service/activity.service';
import { AgentService } from 'src/app/service/agent.service';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { DataService } from 'src/app/service/data.service';
import { EventService } from 'src/app/service/event.service';
import { Events, MsgPayLoad, WebsocketService } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { emptyValidator } from 'src/app/validator/empty-form-validator';
import { Modal } from '../controls/modal/modal.service';
import { SystemsComponent } from '../systems/systems.component';
import { SoftwareInventoryService } from 'src/app/service/software-inventory.service';
import { DiscoverProductsRequest } from 'src/app/model/Product';
import { SelectOption } from '../controls/select/select.component';
import { Environment } from 'src/app/model/SolutionInfo';
import { Agent } from 'src/app/model/Agent';
import { PageEvent } from '../controls/pagination/pagination.component';
import { SolutionService } from 'src/app/service/solution.service';

@Component({
    selector: 'app-systems-register',
    templateUrl: './systems-register.component.html',
    styleUrls: ['./systems-register.component.css'],
    standalone: false
})
export class SystemsRegisterComponent extends SystemsComponent implements OnInit {

  userAuthInfo: UserInfo;
  isRegistering: boolean = false;
  solutionInfoSubscription: Subscription;

  environmentOptions: SelectOption[] = [];
  selectedEnvironment: Environment[] = [];
  selectedSystem: Agent = undefined;
  selectedSystemId: string;

  registerForm = undefined;

  multiColFilter:Object  = undefined;
  pageInfo: PageEvent = undefined;
  height: string = "calc(100vh - 385px)";
  isEnvDropDownOpened: boolean = false;

  constructor(
    protected agentService: AgentService,
    protected eventService: EventService,
    protected router: Router,
    protected dataService: DataService,
    protected route: ActivatedRoute,
    protected modal: Modal,
    protected wsService: WebsocketService,
    protected authService: AuthenticationService,
    protected activityService: ActivityService,
    protected siService: SoftwareInventoryService,
    protected solutionService: SolutionService) {
    super(agentService, eventService, router, dataService, route, modal, wsService, authService, activityService, solutionService);

    if(this.router.currentNavigation() &&
                    this.router.currentNavigation().extras && 
                    this.router.currentNavigation().extras.state){
      this.multiColFilter = this.router.currentNavigation().extras.state.filter;
      this.pageInfo = this.router.currentNavigation().extras.state.page;
    }
  }

  get nameFormCtrl() { return this.registerForm.get('nameFormCtrl'); }
  get descriptionFormCtrl() { return this.registerForm.get('descriptionFormCtrl'); }
  get agreeFormCtrl() { return this.registerForm.get('agreeFormCtrl'); }

  ngOnInit(): void {
    this.title = $localize`Systems - Register`;
    this.routerSubscription = this.route.params.subscribe(params => {
      this.selectedSystemId = params["agentId"]
    });

    this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
      this.solutionInfo = newSolutionInfo;
      this.getAgent();
    });
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    this.solutionInfoSubscription?.unsubscribe();
  }

  cancel(): void {
    this.router.navigate(["/systems/overview"]);
  }

  registerSystem(): void {
    this.isRegistering = true;
    const selectedEnvIds = this.selectedEnvironment.map(env => env.id);
    this.agentService.registerAgent(this.selectedSystem.macAddress, this.nameFormCtrl.value, this.descriptionFormCtrl.value, selectedEnvIds).pipe(
      tap(resp => {
        if (resp.body.success) {
          this.siService.sendDiscoverProducts(this.wsService.getClientId(), this.selectedEnvironment[0].id, this.addTransaction(),
            new DiscoverProductsRequest(this.selectedSystem.macAddress, this.authService.getLoggedInUser().userName, true)).pipe(
          tap(resp => {
            this.router.navigate(["/systems/overview"]);
          }),
          catchError(resp => {
            Utils.addTaskErrorHeaderMessageEventBanner(this.eventService, "DISCOVER_PRODUCTS", this.selectedSystem.systemName);
            return of(resp);
          })).subscribe();
        } else {
          let errorMessage: string = "";
          if (resp.body.errorCode != "APPROVE_AGENT_FAILED") {
            //only one error code exists in the server code so far
            errorMessage = $localize`The system has failed to register the agent, please check the logs for more information.`;
          } else {
            errorMessage = $localize`Failed to register agent.`;
          }

          Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, errorMessage);
        }
      }), 
      finalize(() => {
        this.isRegistering = false;
      })).subscribe();
  }

  onEnvironmentSelected(items: SelectOption[]): void {
    this.selectedEnvironment = [];

    if(items){
        items.forEach((item) => {
            this.selectedEnvironment.push(item.value);
        });
    }
  }

  getAgent(): void {
    this.agentService.getAgent(this.selectedSystemId).pipe(
      tap(resp => {
        this.selectedSystem = resp.body;
        this.selectedSystem.nickName = this.selectedSystem.systemName;

        this.height = this.selectedSystem.isOnline ? "calc(100vh - 385px)" : "calc(100vh - 450px)";

        this.registerForm = new FormGroup({
          nameFormCtrl: new FormControl('', [
            Validators.required, emptyValidator()
          ], [this.validateName(this.selectedSystem.macAddress)]),
          descriptionFormCtrl: new FormControl('', {nonNullable: true}),
          agreeFormCtrl: new FormControl<boolean>(false,
            [Validators.requiredTrue])
        });
        
        if(this.solutionInfo.environments && this.solutionInfo.environments.length > 0){
          let options: SelectOption[] = [];
          let isSelected:boolean = false;
          let canRegisteredToEnv:boolean = false;
          let optionName:string = "";
          let envlist = this.solutionInfo.environments.sort((a, b) => (`${a.solName} : ${a.name}` < `${b.solName} : ${b.name}` ? -1 : 1));
          envlist.forEach(env => {
            canRegisteredToEnv = this.selectedSystem.environments.filter(agentEnv => {return agentEnv.id == env.id}).length == 0;
            isSelected = canRegisteredToEnv && env.id == this.solutionInfo.currentEnvironment.id;
            if(isSelected){
              this.selectedEnvironment.push(env);
            }
            optionName = canRegisteredToEnv ? $localize`${env.solName} : ${env.name}` : $localize`${env.solName} : ${env.name} - registered`;
            options.push(new SelectOption(env, optionName, isSelected, !canRegisteredToEnv));
          });
          this.environmentOptions = options;
        }

        setTimeout(() => {
          this.nameFormCtrl.reset(this.selectedSystem.nickName);
          this.descriptionFormCtrl.reset(this.selectedSystem.description);
          this.agreeFormCtrl.reset(false);

          if(this.selectedSystem.isOnline){
            this.nameFormCtrl.enable();
            this.descriptionFormCtrl.enable();
            this.agreeFormCtrl.enable();
          } else {
            this.nameFormCtrl.disable();
            this.descriptionFormCtrl.disable();
            this.agreeFormCtrl.disable();
          }
        }, 0);
      }),
      catchError(resp => {
        let msg = new MsgPayLoad(undefined, {
          message: `Error getting the system information. Please try again later.`
        });
        msg.setEvent(Events.ErrorMsg);
        Utils.addHeaderMessageBanner(this.eventService, msg);
        this.cancel();
        return of(resp);
      })).subscribe();
  }

  isSelectedSystem(): boolean {
    return this.selectedSystem !== undefined;
  }

  adjustHeight(isOpen:boolean): void{
    this.isEnvDropDownOpened= isOpen;
  }
}